import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEmbeddedEffect } from '@machinemetrics/mm-react-embeddable';
import i18n from 'i18next';

import { actionCloseShiftHandover } from 'lib/actions';
import { getShiftHandoverOpen } from 'lib/selectors/getShiftHandoverOpen';
import { SHIFT_HANDOVER_HOST } from 'lib/appConfig';
import { getLatestOperator } from 'lib/selectors/getLatestOperator';
import { getOperators } from 'lib/selectors/getOperators';
import {
  getShiftHandoverTimesheetRef,
  getShiftHandoverSource,
  getShiftHandoverOperator,
} from 'lib/selectors/getShiftHandoverTimesheetRef';
import MMEmbeddableZoneWithLoader from 'components/MMEmbeddableZoneWithLoader';
import MMDrawer from 'components/MMDrawer';
import { useTranslation } from 'react-i18next';

const EmbeddedShiftHandover = ({
  id,
  operator,
  timesheetRef,
  outgoing,
  onClose,
}) => {
  const [path, setPath] = useState('/handover');

  useEmbeddedEffect(id, 'shift-handover:close', () => {
    onClose();
  });

  useEmbeddedEffect(id, 'set-path', (data) => {
    if (data?.path) {
      setPath(data.path);
    }
  });

  useEmbeddedEffect(id, 'shift-handover:handle-submit', () => {
    onClose();
  });

  const context = {
    operatorId: operator?.id, // Internal operator ID
    operatorErpId: operator?.erpId, // ERP ID (personId from labor tickets)
    operatorName: operator?.name,
    timesheetRef,
    outgoing,
  };

  return (
    <MMEmbeddableZoneWithLoader
      id={id}
      src={`${SHIFT_HANDOVER_HOST}`}
      path={path}
      context={context}
      allow="microphone"
      params={{
        language: i18n.language,
      }}
    />
  );
};

export const ShiftHandoverWrapper = () => {
  const { t } = useTranslation();
  const id = 'shift-handover';
  const dispatch = useDispatch();
  const shiftHandoverOpen = useSelector(getShiftHandoverOpen);
  const outgoingOperator = useSelector(getShiftHandoverOperator);
  const latestOperator = useSelector(getLatestOperator);
  const operators = useSelector(getOperators);
  const timesheetRef = useSelector(getShiftHandoverTimesheetRef);
  const source = useSelector(getShiftHandoverSource);

  // Recover an incomplete snapshot when operators finish loading, without
  // pairing its pinned period with a newer operator from run polling.
  const recoveredOperator =
    outgoingOperator?.id == null && outgoingOperator?.erpId
      ? operators.find((candidate) => {
          return candidate.erpId === outgoingOperator.erpId;
        })
      : null;
  const operator =
    (source !== 'menu' && (recoveredOperator || outgoingOperator)) ||
    latestOperator;
  const operatorName = operator?.name || operator?.erpId;

  // Clock-out handover opens after the operator has already been clocked out (see
  // ClockOutModal single-ticket path and ClockOutEmbedWrapper 'clocked-out'
  // handler); the menu can also open it during an active shift. Every exit closes
  // the drawer. No timesheet mutation here.
  const handleClose = () => {
    dispatch(actionCloseShiftHandover());
  };

  return (
    <MMDrawer
      isOpen={shiftHandoverOpen}
      onClose={handleClose}
      title={
        operatorName
          ? t('hello, {{name}}', { name: operatorName })
          : t('hello operator')
      }
    >
      <EmbeddedShiftHandover
        id={id}
        operator={{ ...operator, name: operatorName }}
        timesheetRef={timesheetRef}
        outgoing={source !== 'menu'}
        onClose={handleClose}
      />
    </MMDrawer>
  );
};
